import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { env } from '@/shared/lib/env';
import { showToast } from '@/shared/hooks/useToast';

type GoogleLoginButtonProps = {
  isLoading: boolean;
  isInFlutter: boolean;
  onIdToken: (idToken: string) => void;
  onRequestNative: () => boolean;
};

export const FeaturesAuthGoogleLoginButton = ({
  isLoading,
  isInFlutter,
  onIdToken,
  onRequestNative,
}: GoogleLoginButtonProps) => {
  const { t, i18n } = useTranslation('login');
  const locale = (i18n.resolvedLanguage ?? i18n.language ?? 'uz').slice(0, 2);
  const handleNative = useCallback((): void => {
    const ok = onRequestNative();
    if (!ok) {
      showToast({ kind: 'error', message: t('bridgeNoResponse') });
    }
  }, [onRequestNative, t]);

  if (isInFlutter) {
    return (
      <Button
        type="button"
        variant="primary"
        size="lg"
        block
        onClick={handleNative}
        loading={isLoading}
      >
        {t('googleSignIn')}
      </Button>
    );
  }

  if (!env.googleClientId) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <p className="font-semibold">{t('googleNotConfigured')}</p>
        <p className="mt-1">{t('googleConfigHint')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      {isLoading ? (
        <Spinner label={t('signingIn')} />
      ) : (
        <GoogleLogin
          onSuccess={(credential) => {
            if (credential.credential) onIdToken(credential.credential);
          }}
          onError={() => showToast({ kind: 'error', message: t('googleCancelled') })}
          width="320"
          shape="pill"
          theme="filled_blue"
          locale={locale}
          text="continue_with"
        />
      )}
    </div>
  );
};
